import type { Portfolio } from 'venn-api';
import { isNil } from 'lodash';
import { isCashNode, isFundNode } from 'venn-utils';

export const getProFormaPortfolioTotalNetAssets = (portfolio: Portfolio | undefined): number => {
  if (isNil(portfolio)) {
    return 0;
  }

  if (isFundNode(portfolio)) {
    return portfolio.allocation ?? 0;
  }

  if (isCashNode(portfolio)) {
    return portfolio.cashBalance ?? 0;
  }

  return portfolio.children?.map((child) => getProFormaPortfolioTotalNetAssets(child)).reduce((a, b) => a + b, 0) ?? 0;
};

export const getHistoricalPortfolioDateTotalAllocations = (
  portfolio: Portfolio | undefined,
  allocationDates: number[],
): Record<number, number> => {
  const dateAllocations = {};

  if (!portfolio) {
    return dateAllocations;
  }

  allocationDates.forEach((date) =>
    getHistoricalPortfolioSingleDateTotalAllocations(portfolio, allocationDates, date, dateAllocations),
  );

  return dateAllocations;
};

const getHistoricalPortfolioSingleDateTotalAllocations = (
  portfolio: Portfolio,
  allocationDates: number[],
  date: number,
  dateAllocations: {
    [id: number]: number;
  },
) => {
  if (portfolio.fund) {
    // fill forward last allocation for display
    dateAllocations[date] =
      (dateAllocations[date] ?? 0) +
      (portfolio.closingAllocationsTs?.sort((a, b) => a[0]! - b[0]!).findLast((x) => x[0]! <= date)?.[1] ?? 0);
  } else {
    portfolio.children?.forEach((child) =>
      getHistoricalPortfolioSingleDateTotalAllocations(child, allocationDates, date, dateAllocations),
    );
  }
};

export const getLotsTotalQuantity = (portfolio: Portfolio | undefined): number => {
  if (!portfolio) {
    return 0;
  }

  const nodeLotsQuantity = Array.isArray(portfolio.lots)
    ? portfolio.lots.reduce((sum, lot) => sum + (lot?.quantity ?? 0), 0)
    : 0;
  const childrenQuantity = Array.isArray(portfolio.children)
    ? portfolio.children.reduce((sum, child) => sum + getLotsTotalQuantity(child), 0)
    : 0;

  return nodeLotsQuantity + childrenQuantity;
};
